import { useState, useEffect } from 'react';
import ProductDetailModal from './ProductDetailModal';

interface Product {
  id: string;
  name: string;
  brand: string;
  category: string;
  sport: string[];
  price: number;
  originalPrice?: number;
  image: string;
  features: string[];
  safetyRating: number;
  performanceBoost: string;
  inStock: boolean;
  isValentine?: boolean;
  isTrending?: boolean;
}

export default function EquipmentStore() {
  const [selectedSport, setSelectedSport] = useState<string>('All');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [processingOrder, setProcessingOrder] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [basket, setBasket] = useState<string[]>([]);
  const [showBasketPanel, setShowBasketPanel] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<{ src: string; name: string } | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  // Load basket from localStorage on mount
  useEffect(() => {
    const savedBasket = localStorage.getItem('equipmentBasket');
    if (savedBasket) {
      setBasket(JSON.parse(savedBasket));
    }
  }, []);

  // Save basket to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('equipmentBasket', JSON.stringify(basket));
  }, [basket]);

  const addToBasket = (productId: string) => {
    if (!basket.includes(productId)) {
      setBasket([...basket, productId]);
    }
  };

  const removeFromBasket = (productId: string) => {
    setBasket(basket.filter(id => id !== productId));
  };

  const isInBasket = (productId: string) => basket.includes(productId);

  const products: Product[] = [
    // VALENTINE'S DAY SPECIAL - MAKEUP & BEAUTY
    {
      id: 'val-makeup-001',
      name: 'Charlotte Tilbury Pillow Talk Lip Set',
      brand: 'Charlotte Tilbury',
      category: 'Makeup',
      sport: ['Valentine'],
      price: 49.99,
      originalPrice: 75.00,
      image: 'https://public.readdy.ai/ai/img_res/226caaece032533cfb119de69d566d95.jpg',
      features: ['Iconic Pillow Talk shade', 'Matte Revolution lipstick', 'Lip Cheat liner included', 'Gift box packaging', '💕 VALENTINE SPECIAL'],
      safetyRating: 5,
      performanceBoost: 'Save 33%',
      inStock: true,
      isValentine: true,
      isTrending: true
    },
    {
      id: 'val-makeup-002',
      name: 'Rare Beauty Soft Pinch Blush Duo',
      brand: 'Rare Beauty',
      category: 'Makeup',
      sport: ['Valentine'],
      price: 38.99,
      originalPrice: 52.00,
      image: 'https://readdy.ai/api/search-image?query=Rare%20Beauty%20Soft%20Pinch%20liquid%20blush%20duo%20set%20in%20pink%20and%20peach%20shades%20with%20minimalist%20packaging%2C%20Selena%20Gomez%20beauty%20brand%20makeup%2C%20dewy%20natural%20flush%20cosmetics%20on%20white%20background&width=400&height=400&seq=rare-beauty-blush-02&orientation=squarish',
      features: ['Buildable formula', 'Long-lasting 12hr wear', 'Two bestselling shades', 'Clean beauty certified', '🔥 TRENDING'],
      safetyRating: 5,
      performanceBoost: 'Save 25%',
      inStock: true,
      isValentine: true,
      isTrending: true
    },
    {
      id: 'val-makeup-003',
      name: 'MAC Ruby Woo Lipstick Gift Set',
      brand: 'MAC',
      category: 'Makeup',
      sport: ['Valentine'],
      price: 42.99,
      originalPrice: 58.00,
      image: 'https://readdy.ai/api/search-image?query=MAC%20Ruby%20Woo%20iconic%20red%20matte%20lipstick%20gift%20set%20with%20matching%20lip%20pencil%20in%20sleek%20black%20packaging%2C%20classic%20red%20lipstick%20for%20valentines%20day%2C%20professional%20makeup%20on%20white%20background&width=400&height=400&seq=mac-rubywoo-set-03&orientation=squarish',
      features: ['Iconic Ruby Woo red', 'Retro matte finish', 'Cherry lip pencil', 'Cult favorite shade', '❤️ CLASSIC RED'],
      safetyRating: 5,
      performanceBoost: 'Save 26%',
      inStock: true,
      isValentine: true
    },
    {
      id: 'val-makeup-004',
      name: 'Fenty Beauty Gloss Bomb Collection',
      brand: 'Fenty Beauty',
      category: 'Makeup',
      sport: ['Valentine'],
      price: 54.99,
      originalPrice: 78.00,
      image: 'https://readdy.ai/api/search-image?query=Fenty%20Beauty%20Gloss%20Bomb%20lip%20gloss%20collection%20set%20with%20multiple%20shades%20in%20sleek%20packaging%2C%20Rihanna%20beauty%20brand%20universal%20lip%20luminizer%2C%20shimmery%20glossy%20lips%20cosmetics%20on%20white%20background&width=400&height=400&seq=fenty-glossbomb-04&orientation=squarish',
      features: ['3 bestselling shades', 'Universal luminizer', 'Non-sticky formula', 'Shea butter enriched', '✨ BESTSELLER'],
      safetyRating: 5,
      performanceBoost: 'Save 30%',
      inStock: true,
      isValentine: true,
      isTrending: true
    },
    {
      id: 'val-makeup-005',
      name: 'NARS Orgasm Blush & Lip Duo',
      brand: 'NARS',
      category: 'Makeup',
      sport: ['Valentine'],
      price: 45.99,
      originalPrice: 62.00,
      image: 'https://readdy.ai/api/search-image?query=NARS%20Orgasm%20blush%20and%20lip%20gloss%20duo%20set%20in%20iconic%20peachy%20pink%20shade%20with%20gold%20shimmer%2C%20luxury%20makeup%20gift%20set%20in%20black%20packaging%2C%20premium%20cosmetics%20on%20white%20background&width=400&height=400&seq=nars-orgasm-duo-05&orientation=squarish',
      features: ['Iconic Orgasm shade', 'Powder blush + lip gloss', 'Golden shimmer finish', 'Award-winning formula', '🏆 AWARD WINNER'],
      safetyRating: 5,
      performanceBoost: 'Save 26%',
      inStock: true,
      isValentine: true
    },
    {
      id: 'val-makeup-006',
      name: 'Dior Addict Lip Maximizer Set',
      brand: 'Dior',
      category: 'Makeup',
      sport: ['Valentine'],
      price: 68.99,
      originalPrice: 92.00,
      image: 'https://readdy.ai/api/search-image?query=Dior%20Addict%20Lip%20Maximizer%20plumping%20gloss%20set%20in%20pink%20and%20berry%20shades%20with%20elegant%20Dior%20packaging%2C%20luxury%20lip%20care%20and%20color%20duo%2C%20high%20end%20cosmetics%20on%20white%20background&width=400&height=400&seq=dior-lipmax-set-06&orientation=squarish',
      features: ['Plumping hyaluronic acid', 'Two luxe shades', 'Cooling sensation', 'Visible volume boost', '💋 PLUMPING'],
      safetyRating: 5,
      performanceBoost: 'Save 25%',
      inStock: true,
      isValentine: true,
      isTrending: true
    },
    {
      id: 'val-makeup-007',
      name: 'Too Faced Better Than Sex Mascara Duo',
      brand: 'Too Faced',
      category: 'Makeup',
      sport: ['Valentine'],
      price: 36.99,
      originalPrice: 54.00,
      image: 'https://readdy.ai/api/search-image?query=Too%20Faced%20Better%20Than%20Sex%20mascara%20duo%20set%20with%20full%20size%20and%20travel%20size%20in%20pink%20packaging%2C%20volumizing%20black%20mascara%20for%20dramatic%20lashes%2C%20popular%20makeup%20on%20white%20background&width=400&height=400&seq=toofaced-mascara-07&orientation=squarish',
      features: ['Full size + travel size', 'Intense black pigment', 'Hourglass wand', 'Film-forming polymers', '👁️ VOLUME BOOST'],
      safetyRating: 5,
      performanceBoost: 'Save 32%',
      inStock: true,
      isValentine: true,
      isTrending: true
    },
    {
      id: 'val-makeup-008',
      name: 'Urban Decay Naked Eyeshadow Palette',
      brand: 'Urban Decay',
      category: 'Makeup',
      sport: ['Valentine'],
      price: 44.99,
      originalPrice: 59.00,
      image: 'https://readdy.ai/api/search-image?query=Urban%20Decay%20Naked%20eyeshadow%20palette%20with%20rose%20gold%20and%20neutral%20shades%20in%20sleek%20compact%2C%20versatile%20everyday%20eye%20makeup%20with%20mirror%2C%20professional%20cosmetics%20on%20white%20background&width=400&height=400&seq=ud-naked-palette-08&orientation=squarish',
      features: ['12 versatile shades', 'Velvety smooth formula', 'Built-in mirror', 'Day to night looks', '🌹 ROSE GOLD'],
      safetyRating: 5,
      performanceBoost: 'Save 24%',
      inStock: true,
      isValentine: true
    },
    // VALENTINE'S DAY - ACCESSORIES & JEWELRY
    {
      id: 'val-acc-001',
      name: 'Pandora Heart Charm Bracelet Set',
      brand: 'Pandora',
      category: 'Accessories',
      sport: ['Valentine'],
      price: 129.99,
      originalPrice: 175.00,
      image: 'https://readdy.ai/api/search-image?query=Pandora%20sterling%20silver%20charm%20bracelet%20with%20heart%20shaped%20charms%20and%20rose%20gold%20accents%2C%20romantic%20jewelry%20gift%20set%20for%20valentines%20day%2C%20elegant%20accessories%20on%20soft%20pink%20background&width=400&height=400&seq=pandora-heart-set-01&orientation=squarish',
      features: ['Sterling silver bracelet', '3 heart charms included', 'Rose gold accents', 'Gift box included', '💝 ROMANTIC GIFT'],
      safetyRating: 5,
      performanceBoost: 'Save 26%',
      inStock: true,
      isValentine: true,
      isTrending: true
    },
    {
      id: 'val-acc-002',
      name: 'Kate Spade Heart Earrings & Necklace',
      brand: 'Kate Spade',
      category: 'Accessories',
      sport: ['Valentine'],
      price: 88.99,
      originalPrice: 128.00,
      image: 'https://readdy.ai/api/search-image?query=Kate%20Spade%20New%20York%20heart%20shaped%20stud%20earrings%20and%20pendant%20necklace%20set%20in%20rose%20gold%20with%20crystal%20accents%2C%20elegant%20jewelry%20gift%20set%2C%20feminine%20accessories%20on%20white%20background&width=400&height=400&seq=katespade-heart-set-02&orientation=squarish',
      features: ['Rose gold plated', 'Crystal embellishments', 'Matching set', 'Signature gift box', '✨ ELEGANT SET'],
      safetyRating: 5,
      performanceBoost: 'Save 30%',
      inStock: true,
      isValentine: true
    },
    {
      id: 'val-acc-003',
      name: 'Swarovski Crystal Heart Pendant',
      brand: 'Swarovski',
      category: 'Accessories',
      sport: ['Valentine'],
      price: 119.99,
      originalPrice: 159.00,
      image: 'https://readdy.ai/api/search-image?query=Swarovski%20crystal%20heart%20pendant%20necklace%20with%20sparkling%20pink%20crystals%20on%20silver%20chain%2C%20luxury%20jewelry%20for%20valentines%20day%20gift%2C%20premium%20accessories%20on%20white%20background&width=400&height=400&seq=swarovski-heart-03&orientation=squarish',
      features: ['Genuine Swarovski crystals', 'Rhodium plated chain', 'Pink crystal heart', 'Certificate included', '💎 LUXURY'],
      safetyRating: 5,
      performanceBoost: 'Save 25%',
      inStock: true,
      isValentine: true,
      isTrending: true
    },
    {
      id: 'val-acc-004',
      name: 'Michael Kors Rose Gold Watch',
      brand: 'Michael Kors',
      category: 'Accessories',
      sport: ['Valentine'],
      price: 179.99,
      originalPrice: 275.00,
      image: 'https://readdy.ai/api/search-image?query=Michael%20Kors%20womens%20rose%20gold%20watch%20with%20crystal%20bezel%20and%20pink%20dial%2C%20elegant%20designer%20timepiece%20for%20valentines%20gift%2C%20luxury%20accessories%20on%20white%20background&width=400&height=400&seq=mk-rosegold-watch-04&orientation=squarish',
      features: ['Rose gold tone', 'Crystal bezel', 'Water resistant', '2-year warranty', '⌚ DESIGNER'],
      safetyRating: 5,
      performanceBoost: 'Save 35%',
      inStock: true,
      isValentine: true,
      isTrending: true
    },
    {
      id: 'val-acc-005',
      name: 'Coach Signature Heart Bag Charm',
      brand: 'Coach',
      category: 'Accessories',
      sport: ['Valentine'],
      price: 58.99,
      originalPrice: 85.00,
      image: 'https://readdy.ai/api/search-image?query=Coach%20signature%20heart%20shaped%20bag%20charm%20keychain%20in%20pink%20leather%20with%20gold%20hardware%2C%20designer%20accessory%20for%20handbag%2C%20luxury%20gift%20on%20white%20background&width=400&height=400&seq=coach-heart-charm-05&orientation=squarish',
      features: ['Genuine leather', 'Gold-tone hardware', 'Signature C pattern', 'Clip attachment', '👜 BAG CHARM'],
      safetyRating: 5,
      performanceBoost: 'Save 31%',
      inStock: true,
      isValentine: true
    },
    {
      id: 'val-acc-006',
      name: 'Tory Burch Pearl Stud Earrings',
      brand: 'Tory Burch',
      category: 'Accessories',
      sport: ['Valentine'],
      price: 68.99,
      originalPrice: 98.00,
      image: 'https://readdy.ai/api/search-image?query=Tory%20Burch%20pearl%20stud%20earrings%20with%20gold%20logo%20detail%2C%20elegant%20designer%20jewelry%20for%20women%2C%20classic%20accessories%20on%20white%20background&width=400&height=400&seq=toryburch-pearl-06&orientation=squarish',
      features: ['Faux pearl center', 'Gold-tone logo', 'Post back closure', 'Gift pouch included', '🦢 CLASSIC'],
      safetyRating: 5,
      performanceBoost: 'Save 30%',
      inStock: true,
      isValentine: true
    },
    // VALENTINE'S DAY - SKINCARE & FRAGRANCE
    {
      id: 'val-skin-001',
      name: 'La Mer Moisturizing Cream Mini Set',
      brand: 'La Mer',
      category: 'Skincare',
      sport: ['Valentine'],
      price: 149.99,
      originalPrice: 210.00,
      image: 'https://readdy.ai/api/search-image?query=La%20Mer%20Creme%20de%20la%20Mer%20moisturizing%20cream%20mini%20set%20with%20travel%20sizes%20in%20elegant%20packaging%2C%20luxury%20skincare%20gift%20set%2C%20premium%20beauty%20on%20white%20background&width=400&height=400&seq=lamer-cream-set-01&orientation=squarish',
      features: ['Miracle Broth formula', '3 mini sizes', 'Travel-friendly', 'Luxury gift box', '🌊 LUXURY SKINCARE'],
      safetyRating: 5,
      performanceBoost: 'Save 29%',
      inStock: true,
      isValentine: true,
      isTrending: true
    },
    {
      id: 'val-skin-002',
      name: 'Drunk Elephant Skincare Duo',
      brand: 'Drunk Elephant',
      category: 'Skincare',
      sport: ['Valentine'],
      price: 78.99,
      originalPrice: 112.00,
      image: 'https://readdy.ai/api/search-image?query=Drunk%20Elephant%20skincare%20duo%20set%20with%20Protini%20moisturizer%20and%20C-Firma%20serum%20in%20colorful%20packaging%2C%20clean%20beauty%20gift%20set%2C%20popular%20skincare%20on%20white%20background&width=400&height=400&seq=drunkelephant-duo-02&orientation=squarish',
      features: ['Protini moisturizer', 'C-Firma vitamin C', 'Clean ingredients', 'Visible results', '🐘 CLEAN BEAUTY'],
      safetyRating: 5,
      performanceBoost: 'Save 29%',
      inStock: true,
      isValentine: true,
      isTrending: true
    },
    {
      id: 'val-frag-001',
      name: 'YSL Libre Eau de Parfum Set',
      brand: 'YSL',
      category: 'Fragrance',
      sport: ['Valentine'],
      price: 124.99,
      originalPrice: 168.00,
      image: 'https://readdy.ai/api/search-image?query=YSL%20Libre%20eau%20de%20parfum%20gift%20set%20with%20full%20size%20bottle%20and%20travel%20spray%20in%20elegant%20black%20and%20gold%20packaging%2C%20luxury%20womens%20fragrance%20for%20valentines%2C%20premium%20perfume%20on%20white%20background&width=400&height=400&seq=ysl-libre-set-01&orientation=squarish',
      features: ['Full size 50ml', 'Travel spray 10ml', 'Lavender & orange blossom', 'Iconic scent', '🖤 BESTSELLER'],
      safetyRating: 5,
      performanceBoost: 'Save 26%',
      inStock: true,
      isValentine: true,
      isTrending: true
    },
    {
      id: 'val-frag-002',
      name: 'Miss Dior Blooming Bouquet Set',
      brand: 'Dior',
      category: 'Fragrance',
      sport: ['Valentine'],
      price: 138.99,
      originalPrice: 185.00,
      image: 'https://readdy.ai/api/search-image?query=Miss%20Dior%20Blooming%20Bouquet%20perfume%20gift%20set%20with%20elegant%20bottle%20and%20body%20lotion%20in%20pink%20packaging%2C%20romantic%20floral%20fragrance%20for%20valentines%20day%2C%20luxury%20perfume%20on%20white%20background&width=400&height=400&seq=missdior-bloom-set-02&orientation=squarish',
      features: ['50ml EDP', 'Body lotion included', 'Peony & rose notes', 'Romantic scent', '🌸 FLORAL'],
      safetyRating: 5,
      performanceBoost: 'Save 25%',
      inStock: true,
      isValentine: true
    },
    {
      id: 'val-frag-003',
      name: 'Chanel Coco Mademoiselle Duo',
      brand: 'Chanel',
      category: 'Fragrance',
      sport: ['Valentine'],
      price: 158.99,
      originalPrice: 215.00,
      image: 'https://readdy.ai/api/search-image?query=Chanel%20Coco%20Mademoiselle%20perfume%20duo%20set%20with%20eau%20de%20parfum%20and%20body%20cream%20in%20iconic%20Chanel%20packaging%2C%20luxury%20fragrance%20gift%20for%20women%2C%20premium%20perfume%20on%20white%20background&width=400&height=400&seq=chanel-coco-duo-03&orientation=squarish',
      features: ['35ml EDP', 'Body cream 150ml', 'Fresh oriental scent', 'Iconic fragrance', '👑 ICONIC'],
      safetyRating: 5,
      performanceBoost: 'Save 26%',
      inStock: true,
      isValentine: true,
      isTrending: true
    },
    // VALENTINE'S DAY - GIFT SETS
    {
      id: 'val-gift-001',
      name: 'Godiva Chocolate Heart Box',
      brand: 'Godiva',
      category: 'Gifts',
      sport: ['Valentine'],
      price: 44.99,
      originalPrice: 65.00,
      image: 'https://readdy.ai/api/search-image?query=Godiva%20chocolate%20heart%20shaped%20gift%20box%20with%20assorted%20premium%20chocolates%20and%20truffles%2C%20luxury%20valentines%20day%20candy%20gift%2C%20elegant%20red%20and%20gold%20packaging%20on%20white%20background&width=400&height=400&seq=godiva-heart-box-01&orientation=squarish',
      features: ['30 piece assortment', 'Heart-shaped box', 'Premium Belgian chocolate', 'Gift ribbon included', '🍫 SWEET GIFT'],
      safetyRating: 5,
      performanceBoost: 'Save 31%',
      inStock: true,
      isValentine: true
    },
    {
      id: 'val-gift-002',
      name: 'Jo Malone Candle & Cologne Set',
      brand: 'Jo Malone',
      category: 'Gifts',
      sport: ['Valentine'],
      price: 118.99,
      originalPrice: 165.00,
      image: 'https://readdy.ai/api/search-image?query=Jo%20Malone%20London%20candle%20and%20cologne%20gift%20set%20with%20Peony%20and%20Blush%20Suede%20scent%20in%20cream%20packaging%2C%20luxury%20home%20fragrance%20and%20perfume%20duo%2C%20elegant%20gift%20on%20white%20background&width=400&height=400&seq=jomalone-gift-set-02&orientation=squarish',
      features: ['Scented candle', 'Cologne 30ml', 'Peony & Blush Suede', 'Gift box included', '🕯️ LUXURY SET'],
      safetyRating: 5,
      performanceBoost: 'Save 28%',
      inStock: true,
      isValentine: true,
      isTrending: true
    },
    {
      id: 'val-gift-003',
      name: 'Dyson Airwrap Complete',
      brand: 'Dyson',
      category: 'Beauty Tools',
      sport: ['Valentine'],
      price: 499.99,
      originalPrice: 599.99,
      image: 'https://readdy.ai/api/search-image?query=Dyson%20Airwrap%20complete%20styling%20set%20with%20multiple%20attachments%20in%20fuchsia%20and%20nickel%2C%20premium%20hair%20styling%20tool%20gift%2C%20luxury%20beauty%20device%20on%20white%20background&width=400&height=400&seq=dyson-airwrap-03&orientation=squarish',
      features: ['6 attachments', 'Coanda air styling', 'Multiple hair types', 'Storage case', '💨 HOT ITEM'],
      safetyRating: 5,
      performanceBoost: 'Save $100',
      inStock: true,
      isValentine: true,
      isTrending: true
    },
    {
      id: 'val-gift-004',
      name: 'Silk Pillowcase & Eye Mask Set',
      brand: 'Slip',
      category: 'Gifts',
      sport: ['Valentine'],
      price: 89.99,
      originalPrice: 129.00,
      image: 'https://readdy.ai/api/search-image?query=Slip%20silk%20pillowcase%20and%20eye%20mask%20gift%20set%20in%20blush%20pink%20color%20with%20elegant%20packaging%2C%20luxury%20sleep%20accessories%20for%20valentines%2C%20premium%20silk%20bedding%20on%20white%20background&width=400&height=400&seq=slip-silk-set-04&orientation=squarish',
      features: ['100% mulberry silk', 'Queen pillowcase', 'Matching eye mask', 'Anti-aging benefits', '😴 BEAUTY SLEEP'],
      safetyRating: 5,
      performanceBoost: 'Save 30%',
      inStock: true,
      isValentine: true
    },
    // NFL JERSEYS - Lowest to Highest Price
    {
      id: 'nfl-jer-001',
      name: 'NFL Game Day Replica Jersey',
      brand: 'Nike',
      category: 'Jerseys',
      sport: ['NFL'],
      price: 99.99,
      image: 'https://readdy.ai/api/search-image?query=Nike%20NFL%20game%20day%20replica%20football%20jersey%20in%20team%20colors%20with%20stitched%20numbers%20and%20logos%2C%20affordable%20official%20NFL%20apparel%20for%20fans%2C%20modern%20athletic%20uniform%20design%20on%20simple%20white%20background&width=400&height=400&seq=nfl-replica-jersey-01&orientation=squarish',
      features: ['Screen-printed graphics', 'Tagless neck label', 'Mesh side panels', 'Official NFL shield'],
      safetyRating: 3,
      performanceBoost: 'Fan Edition',
      inStock: true
    },
    {
      id: 'nfl-jer-002',
      name: 'NFL Legend Team Jersey',
      brand: 'Nike',
      category: 'Jerseys',
      sport: ['NFL'],
      price: 119.99,
      image: 'https://readdy.ai/api/search-image?query=Nike%20NFL%20Legend%20team%20jersey%20with%20premium%20screen%20print%20details%20and%20player%20numbers%2C%20official%20football%20fan%20apparel%20in%20vibrant%20team%20colors%2C%20comfortable%20fit%20athletic%20uniform%20on%20white%20background&width=400&height=400&seq=nfl-legend-jersey-02&orientation=squarish',
      features: ['Dri-FIT technology', 'Tailored fit', 'Premium team graphics', 'Reinforced seams'],
      safetyRating: 3,
      performanceBoost: '+8% Comfort',
      inStock: true
    },
    {
      id: 'nfl-jer-003',
      name: 'NFL Limited Team Jersey - SPECIAL',
      brand: 'Nike',
      category: 'Jerseys',
      sport: ['NFL'],
      price: 149.99,
      image: 'https://readdy.ai/api/search-image?query=Nike%20NFL%20Limited%20football%20jersey%20with%20twill%20numbers%20and%20embroidered%20patches%2C%20special%20edition%20official%20NFL%20uniform%20in%20premium%20team%20colors%20with%20satin%20twill%20details%2C%20high%20quality%20fan%20apparel%20on%20white%20background&width=400&height=400&seq=nfl-limited-special-03&orientation=squarish',
      features: ['Twill numbers and letters', 'Satin twill woven jock tag', 'Strategic ventilation', 'Limited edition design', '🔥 SPECIAL: Save $30'],
      safetyRating: 4,
      performanceBoost: 'Premium Edition',
      inStock: true
    },
    {
      id: 'nfl-jer-004',
      name: 'NFL Color Rush Limited Jersey',
      brand: 'Nike',
      category: 'Jerseys',
      sport: ['NFL'],
      price: 164.99,
      image: 'https://readdy.ai/api/search-image?query=Nike%20NFL%20Color%20Rush%20limited%20jersey%20in%20bold%20single%20color%20design%20with%20premium%20twill%20details%2C%20special%20edition%20monochrome%20football%20uniform%20with%20player%20numbers%2C%20striking%20athletic%20apparel%20on%20white%20background&width=400&height=400&seq=nfl-color-rush-04&orientation=squarish',
      features: ['Unique Color Rush design', 'Twill applique numbers', 'Mesh side panels', 'Commemorative patches'],
      safetyRating: 4,
      performanceBoost: 'Color Rush Special',
      inStock: true
    },
    {
      id: 'nfl-jer-005',
      name: 'NFL Elite Vapor Untouchable Jersey',
      brand: 'Nike',
      category: 'Jerseys',
      sport: ['NFL'],
      price: 324.99,
      image: 'https://readdy.ai/api/search-image?query=Nike%20Vapor%20Untouchable%20Elite%20NFL%20jersey%20with%20professional%20grade%20stitched%20twill%20numbers%20and%20Nike%20Flyknit%20technology%2C%20authentic%20on-field%20player%20uniform%20in%20team%20colors%20with%20premium%20details%2C%20highest%20quality%20football%20jersey%20on%20white%20background&width=400&height=400&seq=nfl-elite-vapor-05&orientation=squarish',
      features: ['Nike Flyknit construction', 'Authentic on-field design', 'Laser-cut perforations', 'Premium tackle twill numbers', 'Water-repelling finish'],
      safetyRating: 5,
      performanceBoost: '+15% Pro Performance',
      inStock: true
    },
    {
      id: 'nfl-jer-006',
      name: 'NFL Salute to Service Jersey - LIMITED',
      brand: 'Nike',
      category: 'Jerseys',
      sport: ['NFL'],
      price: 179.99,
      image: 'https://readdy.ai/api/search-image?query=Nike%20NFL%20Salute%20to%20Service%20limited%20edition%20jersey%20with%20military%20camo%20accents%20and%20patriotic%20details%2C%20special%20commemorative%20football%20uniform%20honoring%20armed%20forces%20with%20unique%20design%20elements%2C%20exclusive%20NFL%20apparel%20on%20white%20background&width=400&height=400&seq=nfl-salute-service-06&orientation=squarish',
      features: ['Camo design accents', 'Military-inspired patches', 'Limited availability', 'Portion benefits military', '🎖️ LIMITED EDITION'],
      safetyRating: 4,
      performanceBoost: 'Commemorative',
      inStock: true
    },
    {
      id: 'nfl-jer-007',
      name: 'NFL Throwback Legacy Jersey - SPECIAL',
      brand: 'Mitchell &amp; Ness',
      category: 'Jerseys',
      sport: ['NFL'],
      price: 299.99,
      image: 'https://readdy.ai/api/search-image?query=Mitchell%20and%20Ness%20NFL%20throwback%20legacy%20jersey%20in%20vintage%20team%20colors%20with%20retro%20styling%20and%20authentic%20period%20details%2C%20classic%20football%20uniform%20with%20old%20school%20design%2C%20premium%20nostalgic%20sports%20apparel%20on%20white%20background&width=400&height=400&seq=nfl-throwback-legacy-07&orientation=squarish',
      features: ['Authentic throwback design', 'Vintage team logos', 'Premium tackle twill', 'Commemorative jock tag', '🏈 THROWBACK SPECIAL'],
      safetyRating: 4,
      performanceBoost: 'Vintage Collectible',
      inStock: true
    },
    {
      id: 'nfl-jer-008',
      name: 'NFL Pro Bowl Game Jersey',
      brand: 'Nike',
      category: 'Jerseys',
      sport: ['NFL'],
      price: 144.99,
      image: 'https://readdy.ai/api/search-image?query=Nike%20NFL%20Pro%20Bowl%20all%20star%20game%20jersey%20with%20AFC%20or%20NFC%20conference%20colors%20and%20special%20event%20graphics%2C%20official%20pro%20bowl%20football%20uniform%20with%20unique%20design%2C%20commemorative%20NFL%20apparel%20on%20white%20background&width=400&height=400&seq=nfl-pro-bowl-08&orientation=squarish',
      features: ['Pro Bowl event graphics', 'Conference team colors', 'Player name and number', 'Special edition patches'],
      safetyRating: 4,
      performanceBoost: 'All-Star Edition',
      inStock: true
    },
    {
      id: 'nfl-jer-009',
      name: 'NFL Custom Personalized Jersey',
      brand: 'Nike',
      category: 'Jerseys',
      sport: ['NFL'],
      price: 169.99,
      image: 'https://readdy.ai/api/search-image?query=customizable%20Nike%20NFL%20jersey%20with%20blank%20back%20ready%20for%20personalization%2C%20official%20football%20uniform%20in%20team%20colors%20with%20your%20name%20and%20number%20option%2C%20personalized%20NFL%20apparel%20on%20white%20background&width=400&height=400&seq=nfl-custom-jersey-09&orientation=squarish',
      features: ['Your name customization', 'Choose your number', 'Official NFL styling', 'Same quality as player jerseys'],
      safetyRating: 4,
      performanceBoost: 'Personalized',
      inStock: true
    },
    {
      id: 'nfl-jer-010',
      name: 'NFL Women\'s Game Jersey',
      brand: 'Nike',
      category: 'Jerseys',
      sport: ['NFL'],
      price: 99.99,
      image: 'https://readdy.ai/api/search-image?query=Nike%20womens%20NFL%20game%20jersey%20with%20tailored%20feminine%20fit%20in%20team%20colors%2C%20official%20football%20apparel%20designed%20for%20women%20with%20contoured%20silhouette%20and%20player%20details%2C%20athletic%20uniform%20on%20white%20background&width=400&height=400&seq=nfl-womens-jersey-10&orientation=squarish',
      features: ['Tailored feminine fit', 'Stretch fabric panels', 'Dri-FIT technology', 'Official team graphics'],
      safetyRating: 3,
      performanceBoost: 'Women\'s Fit',
      inStock: true
    },
    {
      id: 'nfl-jer-011',
      name: 'NFL Youth Game Jersey',
      brand: 'Nike',
      category: 'Jerseys',
      sport: ['NFL'],
      price: 79.99,
      image: 'https://readdy.ai/api/search-image?query=Nike%20youth%20NFL%20game%20jersey%20in%20team%20colors%20for%20kids%2C%20official%20childrens%20football%20apparel%20with%20player%20numbers%20and%20team%20logos%2C%20youth%20size%20athletic%20uniform%20on%20white%20background&width=400&height=400&seq=nfl-youth-jersey-11&orientation=squarish',
      features: ['Youth sizing', 'Durable construction', 'Screen-printed graphics', 'Official NFL shield'],
      safetyRating: 3,
      performanceBoost: 'Youth Edition',
      inStock: true
    },
    {
      id: 'nfl-jer-012',
      name: 'NFL Super Bowl Limited Jersey - SPECIAL',
      brand: 'Nike',
      category: 'Jerseys',
      sport: ['NFL'],
      price: 189.99,
      image: 'https://readdy.ai/api/search-image?query=Nike%20NFL%20Super%20Bowl%20Limited%20edition%20jersey%20with%20championship%20game%20patches%20and%20commemorative%20details%2C%20special%20event%20football%20uniform%20celebrating%20championship%2C%20premium%20NFL%20collectible%20apparel%20on%20white%20background&width=400&height=400&seq=nfl-superbowl-special-12&orientation=squarish',
      features: ['Super Bowl patches', 'Championship graphics', 'Limited production run', 'Collectible edition', '🏆 SUPER BOWL SPECIAL'],
      safetyRating: 4,
      performanceBoost: 'Championship Edition',
      inStock: true
    },
    {
      id: 'eq-001',
      name: 'Riddell SpeedFlex Diamond Helmet',
      brand: 'Riddell',
      category: 'Helmets',
      sport: ['NFL'],
      price: 449.99,
      image: 'https://readdy.ai/api/search-image?query=Riddell%20SpeedFlex%20Diamond%20football%20helmet%20with%20advanced%20impact%20protection%20technology%2C%20modern%20NFL%20safety%20equipment%2C%20professional%20grade%20helmet%20with%20face%20mask%2C%20sleek%20black%20design%20with%20safety%20padding%20visible%2C%20product%20photography%20on%20white%20background&width=400&height=400&seq=riddell-helmet&orientation=squarish',
      features: ['InSite Impact Response System', 'Flex System for impact absorption', 'Tru-Curve aggressive shell design', 'Quick-release face mask'],
      safetyRating: 5,
      performanceBoost: '+35% Impact Protection',
      inStock: true
    },
    {
      id: 'eq-002',
      name: 'Nike Vapor Elite Cleats',
      brand: 'Nike',
      category: 'Footwear',
      sport: ['NFL', 'Soccer'],
      price: 299.99,
      image: 'https://readdy.ai/api/search-image?query=Nike%20Vapor%20Elite%20football%20cleats%20with%20carbon%20fiber%20plate%2C%20professional%20athletic%20footwear%20with%20aggressive%20traction%20studs%2C%20sleek%20modern%20design%20in%20black%20and%20neon%20accents%2C%20high%20performance%20sports%20shoes%20on%20white%20background&width=400&height=400&seq=nike-cleats&orientation=squarish',
      features: ['Carbon fiber plate', 'Flyknit upper', 'Multi-directional traction', 'Lightweight 8.2oz'],
      safetyRating: 4,
      performanceBoost: '+12% Speed Enhancement',
      inStock: true
    },
    {
      id: 'eq-003',
      name: 'Under Armour Gameday Pro Pads',
      brand: 'Under Armour',
      category: 'Protective Gear',
      sport: ['NFL', 'Hockey'],
      price: 189.99,
      image: 'https://readdy.ai/api/search-image?query=Under%20Armour%20professional%20shoulder%20pads%20with%20advanced%20padding%20technology%2C%20modern%20protective%20sports%20equipment%20with%20ventilation%20system%2C%20black%20tactical%20design%20with%20UA%20branding%2C%20athletic%20safety%20gear%20on%20white%20background&width=400&height=400&seq=ua-pads&orientation=squarish',
      features: ['D3O impact protection', 'Moisture-wicking liner', 'Adjustable straps', 'Ventilated design'],
      safetyRating: 5,
      performanceBoost: '+28% Injury Prevention',
      inStock: true
    },
    {
      id: 'eq-004',
      name: 'Adidas Primeknit Team Jersey',
      brand: 'Adidas',
      category: 'Uniforms',
      sport: ['Soccer', 'Basketball'],
      price: 129.99,
      image: 'https://readdy.ai/api/search-image?query=Adidas%20Primeknit%20professional%20sports%20jersey%20with%20moisture%20wicking%20fabric%2C%20modern%20athletic%20uniform%20in%20teal%20and%20white%20colors%2C%20seamless%20construction%20with%20Adidas%20stripes%2C%20performance%20apparel%20on%20white%20background&width=400&height=400&seq=adidas-jersey&orientation=squarish',
      features: ['Primeknit seamless construction', 'Climacool technology', 'Lightweight 4.8oz', 'Custom team branding'],
      safetyRating: 3,
      performanceBoost: '+8% Mobility',
      inStock: true
    },
    {
      id: 'eq-005',
      name: 'Rawlings Heart of the Hide Glove',
      brand: 'Rawlings',
      category: 'Equipment',
      sport: ['Baseball'],
      price: 349.99,
      image: 'https://readdy.ai/api/search-image?query=Rawlings%20Heart%20of%20the%20Hide%20professional%20baseball%20glove%20in%20rich%20brown%20leather%2C%20premium%20quality%20fielding%20mitt%20with%20detailed%20stitching%20and%20web%20pattern%2C%20classic%20baseball%20equipment%20on%20white%20background&width=400&height=400&seq=rawlings-glove&orientation=squarish',
      features: ['Premium steerhide leather', 'Pro-grade lacing', 'Hand-crafted construction', '12.75" outfield pattern'],
      safetyRating: 4,
      performanceBoost: '+15% Catch Rate',
      inStock: true
    },
    {
      id: 'eq-006',
      name: 'Bauer Vapor Hyperlite Skates',
      brand: 'Bauer',
      category: 'Footwear',
      sport: ['Hockey'],
      price: 899.99,
      image: 'https://readdy.ai/api/search-image?query=Bauer%20Vapor%20Hyperlite%20professional%20hockey%20skates%20with%20carbon%20composite%20boot%2C%20high%20performance%20ice%20skating%20equipment%20in%20black%20with%20red%20accents%2C%20modern%20NHL%20player%20skates%20on%20white%20background&width=400&height=400&seq=bauer-skates&orientation=squarish',
      features: ['Hyperlite carbon composite', 'Asymmetrical toe cap', 'LS5 carbon steel blade', 'Thermoformable boot'],
      safetyRating: 5,
      performanceBoost: '+18% Acceleration',
      inStock: true
    },
    {
      id: 'eq-007',
      name: 'Wilson Evolution Basketball',
      brand: 'Wilson',
      category: 'Equipment',
      sport: ['Basketball'],
      price: 69.99,
      image: 'https://readdy.ai/api/search-image?query=Wilson%20Evolution%20official%20basketball%20with%20premium%20composite%20leather%20cover%2C%20professional%20NBA%20quality%20game%20ball%20with%20deep%20channel%20design%2C%20orange%20basketball%20on%20white%20background&width=400&height=400&seq=wilson-ball&orientation=squarish',
      features: ['Composite leather cover', 'Cushion Core Technology', 'Laid-in composite channels', 'Official size and weight'],
      safetyRating: 3,
      performanceBoost: '+10% Grip Control',
      inStock: true
    },
    {
      id: 'eq-008',
      name: 'CCM Super Tacks Helmet',
      brand: 'CCM',
      category: 'Helmets',
      sport: ['Hockey'],
      price: 279.99,
      image: 'https://readdy.ai/api/search-image?query=CCM%20Super%20Tacks%20professional%20hockey%20helmet%20with%20advanced%20protection%20system%2C%20modern%20NHL%20safety%20equipment%20in%20black%20with%20ventilation%20ports%2C%20protective%20headgear%20with%20cage%20on%20white%20background&width=400&height=400&seq=ccm-helmet&orientation=squarish',
      features: ['R.E.D. system protection', 'Multi-density foam liner', '7-point stabilization', '5-star safety rating'],
      safetyRating: 5,
      performanceBoost: '+38% Concussion Prevention',
      inStock: true
    },
    {
      id: 'eq-009',
      name: 'Mizuno Pro Select Bat',
      brand: 'Mizuno',
      category: 'Equipment',
      sport: ['Baseball'],
      price: 399.99,
      image: 'https://readdy.ai/api/search-image?query=Mizuno%20Pro%20Select%20professional%20baseball%20bat%20in%20black%20with%20silver%20accents%2C%20high%20performance%20maple%20wood%20bat%20with%20sleek%20finish%2C%20MLB%20quality%20equipment%20on%20white%20background&width=400&height=400&seq=mizuno-bat&orientation=squarish',
      features: ['Pro-grade maple wood', 'Balanced swing weight', 'Professional finish', 'Hand-selected timber'],
      safetyRating: 4,
      performanceBoost: '+22% Power Transfer',
      inStock: true
    },
    {
      id: 'eq-010',
      name: 'Puma Future Z Soccer Boots',
      brand: 'Puma',
      category: 'Footwear',
      sport: ['Soccer'],
      price: 249.99,
      image: 'https://readdy.ai/api/search-image?query=Puma%20Future%20Z%20professional%20soccer%20boots%20with%20adaptive%20compression%20band%2C%20modern%20football%20cleats%20in%20neon%20yellow%20and%20black%2C%20high%20performance%20athletic%20footwear%20on%20white%20background&width=400&height=400&seq=puma-boots&orientation=squarish',
      features: ['FUZIONFIT+ compression band', 'Dynamic Motion System', 'GripControl Pro coating', 'Advanced Creator Zones'],
      safetyRating: 4,
      performanceBoost: '+14% Ball Control',
      inStock: true
    },
    {
      id: 'eq-011',
      name: 'Schutt F7 LTD Football Helmet',
      brand: 'Schutt',
      category: 'Helmets',
      sport: ['NFL'],
      price: 499.99,
      image: 'https://readdy.ai/api/search-image?query=Schutt%20F7%20LTD%20professional%20football%20helmet%20with%20TPU%20cushioning%20system%2C%20advanced%20NFL%20safety%20equipment%20in%20matte%20black%20finish%2C%20modern%20protective%20headgear%20with%20face%20mask%20on%20white%20background&width=400&height=400&seq=schutt-helmet&orientation=squarish',
      features: ['TPU cushioning system', 'Tektonic plate technology', '7-point stabilization', '5-star safety rating'],
      safetyRating: 5,
      performanceBoost: '+38% Concussion Prevention',
      inStock: true
    },
    {
      id: 'eq-012',
      name: 'Jordan Team Uniform Set',
      brand: 'Jordan',
      category: 'Uniforms',
      sport: ['Basketball'],
      price: 179.99,
      image: 'https://readdy.ai/api/search-image?query=Jordan%20Brand%20professional%20basketball%20uniform%20set%20with%20jersey%20and%20shorts%2C%20modern%20NBA%20style%20athletic%20apparel%20in%20black%20and%20red%2C%20Dri-FIT%20performance%20fabric%20on%20white%20background&width=400&height=400&seq=jordan-uniform&orientation=squarish',
      features: ['Dri-FIT ADV technology', 'Mesh ventilation panels', 'Stretch fabric', 'Jumpman branding'],
      safetyRating: 3,
      performanceBoost: '+9% Range of Motion',
      inStock: true
    },
    {
      id: 'eq-013',
      name: 'Xenith Shadow XR Helmet',
      brand: 'Xenith',
      category: 'Helmets',
      sport: ['NFL'],
      price: 529.99,
      image: 'https://readdy.ai/api/search-image?query=Xenith%20Shadow%20XR%20professional%20football%20helmet%20with%20shock%20absorbers%20and%20adaptive%20fit%20technology%2C%20cutting%20edge%20NFL%20safety%20equipment%20in%20metallic%20silver%20finish%2C%20futuristic%20protective%20headgear%20on%20white%20background&width=400&height=400&seq=xenith-helmet-new&orientation=squarish',
      features: ['Adaptive Head Protection', 'Shock Bonnet technology', 'Aware-Flow ventilation', 'Custom fit system'],
      safetyRating: 5,
      performanceBoost: '+40% Impact Absorption',
      inStock: true
    },
    {
      id: 'eq-014',
      name: 'Easton Ghost Batting Helmet',
      brand: 'Easton',
      category: 'Helmets',
      sport: ['Baseball'],
      price: 89.99,
      image: 'https://readdy.ai/api/search-image?query=Easton%20Ghost%20professional%20baseball%20batting%20helmet%20with%20dual%20density%20foam%20padding%2C%20modern%20MLB%20safety%20equipment%20in%20matte%20navy%20finish%2C%20lightweight%20protective%20headgear%20on%20white%20background&width=400&height=400&seq=easton-helmet-new&orientation=squarish',
      features: ['Dual-density foam padding', 'Wartech FNC liner', 'Anatomical fit', 'Strategic ventilation'],
      safetyRating: 5,
      performanceBoost: '+25% Head Safety',
      inStock: true
    },
    {
      id: 'eq-015',
      name: 'New Balance FuelCell 4040v6 Cleats',
      brand: 'New Balance',
      category: 'Footwear',
      sport: ['Baseball'],
      price: 139.99,
      image: 'https://readdy.ai/api/search-image?query=New%20Balance%20FuelCell%20baseball%20cleats%20with%20responsive%20cushioning%20and%20metal%20spikes%2C%20professional%20MLB%20footwear%20in%20white%20with%20orange%20accents%2C%20high%20performance%20athletic%20shoes%20on%20white%20background&width=400&height=400&seq=nb-cleats-new&orientation=squarish',
      features: ['FuelCell midsole', 'Metal spike plate', 'Synthetic/mesh upper', 'Lightweight construction'],
      safetyRating: 4,
      performanceBoost: '+16% Explosive Speed',
      inStock: true
    },
    {
      id: 'eq-016',
      name: 'Adidas Predator Edge Cleats',
      brand: 'Adidas',
      category: 'Footwear',
      sport: ['Soccer'],
      price: 279.99,
      image: 'https://readdy.ai/api/search-image?query=Adidas%20Predator%20Edge%20professional%20soccer%20cleats%20with%20rubber%20spines%20for%20ball%20control%2C%20modern%20football%20boots%20in%20black%20with%20red%20accents%2C%20high%20performance%20athletic%20footwear%20on%20white%20background&width=400&height=400&seq=adidas-predator-new&orientation=squarish',
      features: ['Facet frame technology', 'Power Facet rubber spines', 'Primeknit collar', 'Controlframe outsole'],
      safetyRating: 4,
      performanceBoost: '+19% Shot Power',
      inStock: true
    },
    {
      id: 'eq-017',
      name: 'CCM Jetspeed FT6 Skates',
      brand: 'CCM',
      category: 'Footwear',
      sport: ['Hockey'],
      price: 799.99,
      image: 'https://readdy.ai/api/search-image?query=CCM%20Jetspeed%20professional%20hockey%20skates%20with%20one%20piece%20boot%20construction%2C%20high%20performance%20ice%20skating%20equipment%20in%20black%20with%20yellow%20details%2C%20modern%20NHL%20player%20skates%20on%20white%20background&width=400&height=400&seq=ccm-skates-new&orientation=squarish',
      features: ['One-piece boot', 'RFM composite', 'SpeedBlade XS holder', 'Multi-density memory foam'],
      safetyRating: 5,
      performanceBoost: '+20% Agility',
      inStock: true
    },
    {
      id: 'eq-018',
      name: 'Douglas CP Series Shoulder Pads',
      brand: 'Douglas',
      category: 'Protective Gear',
      sport: ['NFL'],
      price: 249.99,
      image: 'https://readdy.ai/api/search-image?query=Douglas%20CP%20professional%20football%20shoulder%20pads%20with%20cantilever%20technology%2C%20modern%20NFL%20protective%20equipment%20in%20black%20with%20padding%20visible%2C%20high%20impact%20safety%20gear%20on%20white%20background&width=400&height=400&seq=douglas-pads-new&orientation=squarish',
      features: ['Cantilever technology', 'Lightweight EVA foam', 'Adjustable arch system', 'Moisture management'],
      safetyRating: 5,
      performanceBoost: '+30% Shoulder Protection',
      inStock: true
    },
    {
      id: 'eq-019',
      name: 'Warrior Alpha DX Shin Guards',
      brand: 'Warrior',
      category: 'Protective Gear',
      sport: ['Hockey'],
      price: 179.99,
      image: 'https://readdy.ai/api/search-image?query=Warrior%20Alpha%20professional%20hockey%20shin%20guards%20with%20anatomical%20fit%20and%20impact%20protection%2C%20modern%20NHL%20protective%20equipment%20in%20black%20with%20ventilation%2C%20athletic%20safety%20gear%20on%20white%20background&width=400&height=400&seq=warrior-shins-new&orientation=squarish',
      features: ['Gel-to-shell technology', 'Custom-molding fit', 'Moisture-wicking sleeve', 'Lightweight protection'],
      safetyRating: 4,
      performanceBoost: '+26% Leg Protection',
      inStock: true
    },
    {
      id: 'eq-020',
      name: 'EvoShield Pro-SRZ Leg Guard',
      brand: 'EvoShield',
      category: 'Protective Gear',
      sport: ['Baseball'],
      price: 79.99,
      image: 'https://readdy.ai/api/search-image?query=EvoShield%20professional%20baseball%20leg%20guard%20with%20custom%20molding%20technology%2C%20modern%20MLB%20protective%20equipment%20in%20navy%20with%20gel-to-shell%20protection%2C%20athletic%20safety%20gear%20on%20white%20background&width=400&height=400&seq=evoshield-leg-new&orientation=squarish',
      features: ['Gel-to-shell technology', 'Custom-molding fit', 'Moisture-wicking sleeve', 'Lightweight protection'],
      safetyRating: 4,
      performanceBoost: '+22% Leg Safety',
      inStock: true
    },
    {
      id: 'eq-021',
      name: 'G-Form Pro-X Shin Guards',
      brand: 'G-Form',
      category: 'Protective Gear',
      sport: ['Soccer'],
      price: 49.99,
      image: 'https://readdy.ai/api/search-image?query=G-Form%20Pro-X%20professional%20soccer%20shin%20guards%20with%20reactive%20protection%20technology%2C%20modern%20football%20protective%20equipment%20in%20black%20with%20flexible%20design%2C%20athletic%20safety%20gear%20on%20white%20background&width=400&height=400&seq=gform-shins-new&orientation=squarish',
      features: ['SmartFlex technology', 'Compression sleeve', 'Machine washable', 'Ultra-lightweight'],
      safetyRating: 4,
      performanceBoost: '+18% Shin Protection',
      inStock: true
    },
    {
      id: 'eq-022',
      name: 'Nike Pro Elite Uniform Set',
      brand: 'Nike',
      category: 'Uniforms',
      sport: ['NFL'],
      price: 249.99,
      image: 'https://readdy.ai/api/search-image?query=Nike%20Pro%20Elite%20professional%20football%20uniform%20set%20with%20jersey%20and%20pants%2C%20modern%20NFL%20athletic%20apparel%20in%20teal%20and%20white%2C%20Dri-FIT%20performance%20fabric%20with%20strategic%20ventilation%20on%20white%20background&width=400&height=400&seq=nike-uniform-new&orientation=squarish',
      features: ['Dri-FIT ADV technology', 'Flyvent mesh panels', 'Stretch zones', 'Integrated padding'],
      safetyRating: 3,
      performanceBoost: '+11% Performance',
      inStock: true
    },
    {
      id: 'eq-023',
      name: 'Majestic Cool Base Jersey',
      brand: 'Majestic',
      category: 'Uniforms',
      sport: ['Baseball'],
      price: 119.99,
      image: 'https://readdy.ai/api/search-image?query=Majestic%20Cool%20Base%20professional%20baseball%20jersey%20with%20moisture%20management%20technology%2C%20modern%20MLB%20athletic%20uniform%20in%20white%20with%20team%20colors%2C%20performance%20apparel%20on%20white%20background&width=400&height=400&seq=majestic-jersey-new&orientation=squarish',
      features: ['Cool Base technology', 'Moisture management', 'Button-front design', 'Team customization'],
      safetyRating: 3,
      performanceBoost: '+7% Comfort',
      inStock: true
    },
    {
      id: 'eq-024',
      name: 'Adidas Tiro Match Jersey',
      brand: 'Adidas',
      category: 'Uniforms',
      sport: ['Soccer'],
      price: 89.99,
      image: 'https://readdy.ai/api/search-image?query=professional%20Adidas%20soccer%20jersey%20in%20vibrant%20orange%20color%20with%20black%20three%20stripe%20details%20on%20shoulders%2C%20modern%20athletic%20football%20uniform%20with%20crew%20neck%20and%20Aeroready%20moisture%20wicking%20fabric%2C%20clean%20product%20photography%20showing%20front%20view%20of%20sports%20apparel%20on%20plain%20white%20studio%20background&width=400&height=400&seq=adidas-tiro-jersey-fixed&orientation=squarish',
      features: ['Aeroready technology', 'Recycled materials', 'Slim fit design', 'Mesh inserts'],
      safetyRating: 3,
      performanceBoost: '+6% Breathability',
      inStock: true
    },
    {
      id: 'eq-025',
      name: 'CCM QuickLite Hockey Jersey',
      brand: 'CCM',
      category: 'Uniforms',
      sport: ['Hockey'],
      price: 159.99,
      image: 'https://readdy.ai/api/search-image?query=CCM%20QuickLite%20professional%20hockey%20jersey%20with%20lightweight%20construction%2C%20modern%20NHL%20athletic%20uniform%20in%20black%20with%20team%20colors%2C%20performance%20apparel%20with%20ventilation%20on%20white%20background&width=400&height=400&seq=ccm-jersey-new&orientation=squarish',
      features: ['QuickLite fabric', 'Strategic ventilation', 'Reinforced elbows', 'Fight strap'],
      safetyRating: 3,
      performanceBoost: '+8% Mobility',
      inStock: true
    },
    {
      id: 'eq-026',
      name: 'Spalding TF-1000 Basketball',
      brand: 'Spalding',
      category: 'Equipment',
      sport: ['Basketball'],
      price: 79.99,
      image: 'https://readdy.ai/api/search-image?query=Spalding%20TF-1000%20professional%20basketball%20with%20premium%20composite%20cover%2C%20high%20quality%20game%20ball%20with%20deep%20pebble%20channels%2C%20orange%20basketball%20on%20white%20background&width=400&height=400&seq=spalding-ball-new&orientation=squarish',
      features: ['ZK microfiber composite', 'Deep channel design', 'Cushioned carcass', 'Official size 7'],
      safetyRating: 3,
      performanceBoost: '+12% Ball Control',
      inStock: true
    },
    {
      id: 'eq-027',
      name: 'Louisville Slugger Prime Bat',
      brand: 'Louisville Slugger',
      category: 'Equipment',
      sport: ['Baseball'],
      price: 449.99,
      image: 'https://readdy.ai/api/search-image?query=Louisville%20Slugger%20Prime%20professional%20baseball%20bat%20with%20composite%20barrel%2C%20high%20performance%20BBCOR%20bat%20in%20black%20with%20red%20accents%2C%20modern%20baseball%20equipment%20on%20white%20background&width=400&height=400&seq=slugger-bat-new&orientation=squarish',
      features: ['3FX connection system', 'EKO composite barrel', 'VCX vibration control', 'BBCOR certified'],
      safetyRating: 4,
      performanceBoost: '+24% Swing Speed',
      inStock: true
    },
    {
      id: 'eq-028',
      name: 'Bauer Supreme Ultrasonic Stick',
      brand: 'Bauer',
      category: 'Equipment',
      sport: ['Hockey'],
      price: 329.99,
      image: 'https://readdy.ai/api/search-image?query=Bauer%20Supreme%20Ultrasonic%20professional%20hockey%20stick%20with%20carbon%20fiber%20construction%2C%20high%20performance%20NHL%20equipment%20in%20black%20with%20yellow%20accents%2C%20modern%20hockey%20stick%20on%20white%20background&width=400&height=400&seq=bauer-stick-new&orientation=squarish',
      features: ['ACL 2.0 technology', 'DuraFlex resin system', 'MaxBalance blade', 'TeXtreme carbon fiber'],
      safetyRating: 4,
      performanceBoost: '+17% Shot Accuracy',
      inStock: true
    },
    {
      id: 'eq-029',
      name: 'Select Brillant Super TB Ball',
      brand: 'Select',
      category: 'Equipment',
      sport: ['Soccer'],
      price: 159.99,
      image: 'https://readdy.ai/api/search-image?query=Select%20Brillant%20Super%20professional%20soccer%20ball%20with%20hand%20stitched%20panels%2C%20premium%20FIFA%20quality%20match%20ball%20in%20white%20with%20colorful%20geometric%20design%2C%20football%20on%20white%20background&width=400&height=400&seq=select-ball-new&orientation=squarish',
      features: ['Hand-stitched construction', 'Zero-wing latex bladder', 'Double-lock valve', 'FIFA Quality Pro'],
      safetyRating: 3,
      performanceBoost: '+13% Flight Stability',
      inStock: true
    },
    {
      id: 'eq-030',
      name: 'Wilson A2000 Catcher Mitt',
      brand: 'Wilson',
      category: 'Equipment',
      sport: ['Baseball'],
      price: 399.99,
      image: 'https://readdy.ai/api/search-image?query=Wilson%20A2000%20professional%20baseball%20catcher%20mitt%20in%20premium%20leather%2C%20high%20quality%20catching%20glove%20with%20deep%20pocket%20and%20reinforced%20palm%2C%20classic%20brown%20baseball%20equipment%20on%20white%20background&width=400&height=400&seq=wilson-catcher-new&orientation=squarish',
      features: ['Pro Stock leather', 'Dual Welting', 'DriLex wrist lining', '33.5" pattern'],
      safetyRating: 4,
      performanceBoost: '+20% Catch Security',
      inStock: true
    },
    // NEW MEMORABILIA PRODUCTS
    {
      id: 'mem-001',
      name: 'Tom Brady Signed Super Bowl Football',
      brand: 'Fanatics',
      category: 'Memorabilia',
      sport: ['NFL'],
      price: 1299.99,
      image: 'https://readdy.ai/api/search-image?query=authentic%20Tom%20Brady%20autographed%20Super%20Bowl%20championship%20football%20in%20display%20case%20with%20certificate%20of%20authenticity%2C%20premium%20NFL%20sports%20memorabilia%20collectible%20with%20signature%20visible%2C%20museum%20quality%20presentation%20on%20white%20background&width=400&height=400&seq=brady-football-mem&orientation=squarish',
      features: ['Authentic autograph', 'Certificate of Authenticity', 'Premium display case', 'Limited edition'],
      safetyRating: 5,
      performanceBoost: 'Investment Grade',
      inStock: true
    },
    {
      id: 'mem-002',
      name: 'Patrick Mahomes Signed Jersey',
      brand: 'Fanatics',
      category: 'Memorabilia',
      sport: ['NFL'],
      price: 899.99,
      image: 'https://readdy.ai/api/search-image?query=Patrick%20Mahomes%20autographed%20Kansas%20City%20Chiefs%20red%20jersey%20in%20premium%20frame%20with%20hologram%20authentication%2C%20NFL%20sports%20memorabilia%20with%20signature%20on%20number%20fifteen%2C%20professional%20framing%20on%20white%20background&width=400&height=400&seq=mahomes-jersey-mem&orientation=squarish',
      features: ['Hand-signed by Mahomes', 'Hologram authentication', 'Professional framing', 'UV protection glass'],
      safetyRating: 5,
      performanceBoost: 'Collector Item',
      inStock: true
    },
    {
      id: 'mem-003',
      name: 'LeBron James Signed Photo',
      brand: 'Upper Deck',
      category: 'Memorabilia',
      sport: ['Basketball'],
      price: 749.99,
      image: 'https://readdy.ai/api/search-image?query=LeBron%20James%20autographed%20action%20photo%20dunking%20basketball%20in%20elegant%20black%20frame%20with%20certificate%2C%20NBA%20sports%20memorabilia%20with%20authentic%20signature%2C%20premium%20quality%20framed%20photograph%20on%20white%20background&width=400&height=400&seq=lebron-photo-mem&orientation=squarish',
      features: ['16x20 signed photo', 'Upper Deck certified', 'Museum-quality frame', 'Numbered edition'],
      safetyRating: 5,
      performanceBoost: 'Investment Piece',
      inStock: true
    },
    {
      id: 'mem-004',
      name: 'Michael Jordan Rookie Card PSA 10',
      brand: 'PSA',
      category: 'Memorabilia',
      sport: ['Basketball'],
      price: 4999.99,
      image: 'https://readdy.ai/api/search-image?query=Michael%20Jordan%201986%20Fleer%20rookie%20basketball%20card%20graded%20PSA%2010%20gem%20mint%20in%20protective%20case%2C%20valuable%20NBA%20sports%20memorabilia%20collectible%20card%2C%20pristine%20condition%20with%20PSA%20authentication%20on%20white%20background&width=400&height=400&seq=jordan-card-mem&orientation=squarish',
      features: ['PSA 10 Gem Mint', '1986 Fleer #57', 'Protective case', 'Investment grade'],
      safetyRating: 5,
      performanceBoost: 'Blue Chip Asset',
      inStock: true
    },
    {
      id: 'mem-005',
      name: 'Derek Jeter Game-Used Bat',
      brand: 'Steiner Sports',
      category: 'Memorabilia',
      sport: ['Baseball'],
      price: 1899.99,
      image: 'https://readdy.ai/api/search-image?query=Derek%20Jeter%20authentic%20game%20used%20baseball%20bat%20from%20New%20York%20Yankees%20with%20visible%20wear%20marks%20and%20authentication%20hologram%2C%20MLB%20sports%20memorabilia%20in%20display%20case%2C%20professional%20presentation%20on%20white%20background&width=400&height=400&seq=jeter-bat-mem&orientation=squarish',
      features: ['Game-used by Jeter', 'Steiner authentication', 'Display case included', 'Photo-matched'],
      safetyRating: 5,
      performanceBoost: 'Museum Quality',
      inStock: true
    },
    {
      id: 'mem-006',
      name: 'Babe Ruth Signed Baseball',
      brand: 'JSA',
      category: 'Memorabilia',
      sport: ['Baseball'],
      price: 8999.99,
      image: 'https://readdy.ai/api/search-image?query=vintage%20Babe%20Ruth%20autographed%20baseball%20with%20clear%20signature%20in%20premium%20glass%20display%20case%2C%20rare%20MLB%20sports%20memorabilia%20collectible%20with%20JSA%20authentication%20certificate%2C%20museum%20quality%20presentation%20on%20white%20background&width=400&height=400&seq=ruth-ball-mem&orientation=squarish',
      features: ['Authentic Ruth signature', 'JSA certified', 'Vintage baseball', 'Investment grade'],
      safetyRating: 5,
      performanceBoost: 'Rare Collectible',
      inStock: true
    },
    {
      id: 'mem-007',
      name: 'Mike Trout Signed Rookie Card',
      brand: 'Topps',
      category: 'Memorabilia',
      sport: ['Baseball'],
      price: 1499.99,
      image: 'https://readdy.ai/api/search-image?query=Mike%20Trout%20autographed%202011%20Topps%20Update%20rookie%20baseball%20card%20with%20on-card%20signature%20in%20protective%20case%2C%20valuable%20MLB%20sports%20memorabilia%20with%20authentication%2C%20pristine%20condition%20on%20white%20background&width=400&height=400&seq=trout-card-mem&orientation=squarish',
      features: ['On-card autograph', '2011 Topps Update', 'BGS graded', 'Limited edition'],
      safetyRating: 5,
      performanceBoost: 'High Value',
      inStock: true
    },
    {
      id: 'mem-008',
      name: 'Lionel Messi Signed Argentina Jersey',
      brand: 'Icons',
      category: 'Memorabilia',
      sport: ['Soccer'],
      price: 2499.99,
      image: 'https://readdy.ai/api/search-image?query=Lionel%20Messi%20autographed%20Argentina%20national%20team%20blue%20and%20white%20striped%20jersey%20in%20premium%20frame%20with%20World%20Cup%20imagery%2C%20soccer%20football%20memorabilia%20with%20authentic%20signature%2C%20professional%20display%20on%20white%20background&width=400&height=400&seq=messi-jersey-mem&orientation=squarish',
      features: ['World Cup jersey', 'Hand-signed by Messi', 'Icons authentication', 'Premium framing'],
      safetyRating: 5,
      performanceBoost: 'Legendary Item',
      inStock: true
    },
    {
      id: 'mem-009',
      name: 'Cristiano Ronaldo Signed Boot',
      brand: 'Icons',
      category: 'Memorabilia',
      sport: ['Soccer'],
      price: 1799.99,
      image: 'https://readdy.ai/api/search-image?query=Cristiano%20Ronaldo%20autographed%20Nike%20soccer%20boot%20cleat%20in%20acrylic%20display%20case%20with%20certificate%2C%20football%20memorabilia%20with%20signature%20on%20white%20boot%2C%20premium%20presentation%20on%20white%20background&width=400&height=400&seq=ronaldo-boot-mem&orientation=squarish',
      features: ['Hand-signed McDavid', 'Fanatics authentic', 'Professional framing', 'Hologram verified'],
      safetyRating: 5,
      performanceBoost: 'Rising Star',
      inStock: true
    },
    {
      id: 'mem-010',
      name: 'Sidney Crosby Game-Used Puck',
      brand: 'NHL',
      category: 'Memorabilia',
      sport: ['Hockey'],
      price: 499.99,
      image: 'https://readdy.ai/api/search-image?query=Sidney%20Crosby%20game%20used%20NHL%20hockey%20puck%20in%20acrylic%20cube%20display%20with%20authentication%20certificate%2C%20Pittsburgh%20Penguins%20sports%20memorabilia%20with%20visible%20ice%20marks%2C%20official%20NHL%20collectible%20on%20white%20background&width=400&height=400&seq=crosby-puck-mem&orientation=squarish',
      features: ['Game-used puck', 'NHL authenticated', 'Acrylic display cube', 'Certificate included'],
      safetyRating: 5,
      performanceBoost: 'Game Artifact',
      inStock: true
    },
    {
      id: 'mem-011',
      name: 'Kobe Bryant Signed Photo 8x10',
      brand: 'Panini',
      category: 'Memorabilia',
      sport: ['Basketball'],
      price: 1999.99,
      image: 'https://readdy.ai/api/search-image?query=Kobe%20Bryant%20autographed%208x10%20action%20photo%20in%20Lakers%20uniform%20in%20elegant%20frame%20with%20Panini%20authentication%2C%20NBA%20sports%20memorabilia%20with%20signature%2C%20memorial%20collectible%20on%20white%20background&width=400&height=400&seq=kobe-photo-mem&orientation=squarish',
      features: ['Authentic Kobe signature', 'Panini certified', '8x10 photo', 'Memorial piece'],
      safetyRating: 5,
      performanceBoost: 'Legacy Item',
      inStock: true
    },
    {
      id: 'mem-012',
      name: 'Stephen Curry Signed Basketball',
      brand: 'Fanatics',
      category: 'Memorabilia',
      sport: ['Basketball'],
      price: 649.99,
      image: 'https://readdy.ai/api/search-image?query=Stephen%20Curry%20autographed%20Spalding%20basketball%20in%20clear%20acrylic%20display%20case%20with%20hologram%20authentication%2C%20Golden%20State%20Warriors%20NBA%20sports%20memorabilia%20with%20signature%2C%20premium%20presentation%20on%20white%20background&width=400&height=400&seq=curry-ball-mem&orientation=squarish',
      features: ['Signed by Curry', 'Official NBA ball', 'Display case', 'Fanatics hologram'],
      safetyRating: 5,
      performanceBoost: 'Championship Era',
      inStock: true
    }
  ];

  // Get basket items with full product details
  const basketItems = products.filter(product => basket.includes(product.id));
  const basketTotal = basketItems.reduce((sum, item) => sum + item.price, 0);
  const basketSavings = basketItems.reduce((sum, item) => {
    if (item.originalPrice) {
      return sum + (item.originalPrice - item.price);
    }
    return sum;
  }, 0);

  const sports = ['All', 'Valentine', 'NFL', 'NBA', 'Baseball', 'Soccer', 'Hockey', 'Basketball'];
  const categories = ['All', 'Makeup', 'Accessories', 'Skincare', 'Fragrance', 'Gifts', 'Beauty Tools', 'Jerseys', 'Helmets', 'Footwear', 'Protective Gear', 'Uniforms', 'Equipment', 'Memorabilia'];

  const filteredProducts = products.filter(product => {
    const sportMatch = selectedSport === 'All' || product.sport.includes(selectedSport);
    const categoryMatch = selectedCategory === 'All' || product.category === selectedCategory;
    return sportMatch && categoryMatch;
  });

  const handleOrder = async (product: Product) => {
    console.log('🛒 Order initiated for:', product.name);
    console.log('📦 Product details:', {
      id: product.id,
      name: product.name,
      price: product.price,
      brand: product.brand
    });
    
    setProcessingOrder(product.id);
    
    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

      console.log('🔧 Environment check:', {
        hasSupabaseUrl: !!supabaseUrl,
        hasSupabaseKey: !!supabaseKey,
        urlPreview: supabaseUrl ? `${supabaseUrl.substring(0, 30)}...` : 'missing'
      });

      // Enhanced validation with user-friendly messages
      if (!supabaseUrl || !supabaseKey) {
        console.error('❌ Missing Supabase configuration');
        throw new Error('Payment system is not configured. Please contact support or try again later.');
      }

      console.log('📡 Creating checkout session...');
      console.log('🔗 Endpoint:', `${supabaseUrl}/functions/v1/create-checkout`);

      // Create checkout session with timeout and detailed logging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.error('⏱️ Request timeout after 15 seconds');
        controller.abort();
      }, 15000);

      const requestBody = {
        product: {
          id: product.id,
          name: product.name,
          brand: product.brand,
          category: product.category,
          price: product.price,
          image: product.image
        },
        successUrl: `${window.location.origin}/?payment=success&session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/?payment=cancelled`
      };

      console.log('📤 Request payload:', requestBody);

      const response = await fetch(`${supabaseUrl}/functions/v1/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': supabaseKey,
          'Authorization': `Bearer ${supabaseKey}`
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      console.log('📥 Response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ 
          error: `Server error: ${response.status} ${response.statusText}` 
        }));
        console.error('❌ Checkout error response:', errorData);
        
        // User-friendly error messages based on status codes
        let userMessage = '';
        if (response.status === 404) {
          userMessage = 'Payment service not found. Please ensure Stripe is properly configured.';
        } else if (response.status === 401 || response.status === 403) {
          userMessage = 'Authentication error. Please refresh the page and try again.';
        } else if (response.status >= 500) {
          userMessage = 'Payment service is temporarily unavailable. Please try again in a few moments.';
        } else {
          userMessage = errorData.error || 'Unable to process payment. Please try again.';
        }
        
        throw new Error(userMessage);
      }

      const data = await response.json();
      console.log('✅ Checkout session created successfully:', {
        hasSessionId: !!data.sessionId,
        hasUrl: !!data.url,
        commissionAmount: data.commissionAmount
      });
      
      if (!data.url) {
        console.error('❌ No checkout URL in response:', data);
        throw new Error('Payment system error: No checkout URL received. Please try again or contact support.');
      }

      // Show loading feedback before redirect
      console.log('🔄 Redirecting to Stripe checkout...');
      console.log('🔗 Checkout URL:', data.url);
      
      // Add a small delay to show processing state
      await new Promise(resolve => setTimeout(resolve, 500));
      
      window.location.href = data.url;
      
    } catch (error) {
      // Normalize error to always get a readable message
      let rawMessage = '';
      if (error instanceof Error) {
        rawMessage = error.message;
      } else if (typeof error === 'string') {
        rawMessage = error;
      } else {
        try {
          rawMessage = JSON.stringify(error);
        } catch {
          rawMessage = 'An unexpected error occurred.';
        }
      }

      console.error('💥 Order processing error:', rawMessage);

      let userMessage = '⚠️ Unable to process your order. ';

      if (error instanceof Error && error.name === 'AbortError') {
        userMessage += 'The request took too long. Please check your internet connection and try again.';
      } else if (
        rawMessage.includes('Failed to fetch') ||
        rawMessage.includes('NetworkError') ||
        rawMessage.includes('Load failed')
      ) {
        userMessage += 'Network connection error. Please check your internet and try again.';
      } else if (
        rawMessage.includes('configuration') ||
        rawMessage.includes('not configured') ||
        rawMessage.includes('Stripe')
      ) {
        userMessage += rawMessage;
      } else if (rawMessage) {
        userMessage += rawMessage;
      } else {
        userMessage += 'An unexpected error occurred. Please try again or contact support.';
      }

      setErrorMessage(userMessage);
      setShowErrorModal(true);
      setProcessingOrder(null);
    }
  };

  // Check for payment status in URL on component mount with logging
  useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const sessionId = urlParams.get('session_id');
    
    console.log('🔍 Checking URL for payment status:', {
      paymentStatus,
      hasSessionId: !!sessionId
    });
    
    if (paymentStatus === 'success') {
      console.log('✅ Payment successful! Session ID:', sessionId);
      setShowSuccessModal(true);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (paymentStatus === 'cancelled') {
      console.log('❌ Payment cancelled by user');
      setErrorMessage('Payment was cancelled. You can try again whenever you\'re ready.');
      setShowErrorModal(true);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  });

  return (
    <section className="bg-gray-50 py-32" id="equipment-store">
      {/* Image Lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button
            className="absolute top-5 right-5 w-10 h-10 bg-white/20 hover:bg-white/40 rounded-full flex items-center justify-center transition-colors cursor-pointer"
            onClick={() => setLightboxImage(null)}
          >
            <i className="ri-close-line text-white text-2xl"></i>
          </button>
          <div
            className="max-w-3xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightboxImage.src}
              alt={lightboxImage.name}
              className="w-full max-h-[80vh] object-contain rounded-2xl shadow-2xl"
            />
            <p className="text-white text-center mt-4 font-semibold text-lg">{lightboxImage.name}</p>
          </div>
        </div>
      )}

      {/* Floating Basket Button */}
      <button
        onClick={() => setShowBasketPanel(true)}
        className="fixed bottom-24 right-6 z-40 bg-gray-900 text-white w-14 h-14 rounded-full shadow-lg hover:bg-gray-800 transition-all hover:scale-110 flex items-center justify-center cursor-pointer"
      >
        <i className="ri-shopping-basket-2-line text-xl"></i>
        {basket.length > 0 && (
          <span className="absolute -top-1 -right-1 bg-teal-500 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center">
            {basket.length}
          </span>
        )}
      </button>

      {/* Basket Panel Overlay */}
      {showBasketPanel && (
        <div 
          className="fixed inset-0 bg-black/50 z-50"
          onClick={() => setShowBasketPanel(false)}
        />
      )}

      {/* Basket Summary Panel */}
      <div 
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-50 transform transition-transform duration-300 ${
          showBasketPanel ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                <i className="ri-shopping-basket-2-line text-teal-600 text-lg"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Your Basket</h3>
                <p className="text-sm text-gray-500 text-xs mb-2">Add items from the store to get started</p>
                <button
                  onClick={() => setShowBasketPanel(false)}
                  className="bg-teal-500 text-white px-6 py-2 rounded-lg font-medium hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Continue Shopping
                </button>
              </div>
            </div>
            <button
              onClick={() => setShowBasketPanel(false)}
              className="w-10 h-10 rounded-full hover:bg-red-100 flex items-center justify-center transition-colors cursor-pointer flex-shrink-0 self-start"
            >
              <i className="ri-close-line text-xl text-gray-500"></i>
            </button>
          </div>

          {/* Basket Items */}
          <div className="flex-1 overflow-y-auto p-6">
            {basketItems.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shopping-basket-line text-3xl text-gray-400"></i>
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Your basket is empty</h4>
                <p className="text-gray-500 text-sm mb-6">Add items from the store to get started</p>
                <button
                  onClick={() => setShowBasketPanel(false)}
                  className="bg-teal-500 text-white px-6 py-2 rounded-lg font-medium hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Continue Shopping
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {basketItems.map(item => (
                  <div 
                    key={item.id}
                    className="bg-gray-50 rounded-xl p-4 flex gap-4"
                  >
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 font-medium">{item.brand}</p>
                      <h4 className="text-sm font-semibold text-gray-900 truncate">{item.name}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-lg font-bold text-gray-900">${item.price.toFixed(2)}</span>
                        {item.originalPrice && (
                          <span className="text-sm text-gray-400 line-through">${item.originalPrice.toFixed(2)}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="bg-teal-100 text-teal-700 text-xs px-2 py-0.5 rounded-full font-medium">
                          {item.category}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => removeFromBasket(item.id)}
                      className="w-8 h-8 rounded-full hover:bg-red-100 flex items-center justify-center transition-colors cursor-pointer flex-shrink-0 self-start"
                    >
                      <i className="ri-delete-bin-line text-red-500"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer with Total */}
          {basketItems.length > 0 && (
            <div className="border-t border-gray-100 p-6 bg-white">
              {basketSavings > 0 && (
                <div className="flex items-center justify-between mb-3 bg-green-50 p-3 rounded-lg">
                  <span className="text-sm text-green-700 font-medium">
                    <i className="ri-discount-percent-line mr-1"></i>
                    You're saving
                  </span>
                  <span className="text-green-700 font-bold">${basketSavings.toFixed(2)}</span>
                </div>
              )}
              
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-600">Subtotal</span>
                <span className="text-gray-900 font-semibold">${basketTotal.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between mb-4">
                <span className="text-gray-600">Shipping</span>
                <span className="text-teal-600 font-medium">FREE</span>
              </div>
              
              <div className="flex items-center justify-between py-4 border-t border-gray-200">
                <span className="text-lg font-bold text-gray-900">Total</span>
                <span className="text-2xl font-bold text-gray-900">${basketTotal.toFixed(2)}</span>
              </div>

              <button
                onClick={() => {
                  setShowBasketPanel(false);
                  // Scroll to first basket item in the store
                  const storeSection = document.getElementById('equipment-store');
                  if (storeSection) {
                    storeSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="w-full bg-teal-500 text-white py-4 rounded-xl font-semibold hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-shopping-cart-line mr-2"></i>
                Checkout All Items
              </button>
              
              <button
                onClick={() => {
                  setBasket([]);
                }}
                className="w-full bg-gray-100 text-gray-700 py-3 rounded-xl font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                Clear Basket
              </button>

              <p className="text-xs text-gray-500 text-center mt-4">
                <i className="ri-shield-check-line mr-1"></i>
                Secure checkout • Free shipping on all orders
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Success Modal - Enhanced */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl animate-fade-in">
            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
                <i className="ri-check-line text-4xl text-green-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">🎉 Payment Successful!</h3>
              <p className="text-gray-600 mb-2">
                Your order has been confirmed and will be processed shortly.
              </p>
              <p className="text-sm text-gray-500 mb-6">
                You'll receive a confirmation email with tracking details within 24 hours.
              </p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-green-800">
                  <strong>What's Next?</strong><br />
                  • Check your email for order confirmation<br />
                  • Track your shipment in the Orders page<br />
                  • Equipment ships directly from manufacturer
                </p>
              </div>
              <button
                onClick={() => {
                  console.log('✅ User closed success modal');
                  setShowSuccessModal(false);
                }}
                className="w-full bg-teal-500 text-white py-3 rounded-lg font-semibold hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Continue Shopping
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal - Enhanced */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl animate-fade-in">
            <div className="text-center">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-error-warning-line text-4xl text-red-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Payment Issue</h3>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-red-800 text-left">{errorMessage}</p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                <p className="text-xs text-gray-600 mb-2"><strong>💡 Troubleshooting Tips:</strong></p>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>• Check your internet connection</li>
                  <li>• Try refreshing the page</li>
                  <li>• Clear browser cache if issue persists</li>
                  <li>• Contact support if problem continues</li>
                </ul>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => {
                    console.log('🔄 User clicked Try Again');
                    setShowErrorModal(false);
                    setErrorMessage('');
                  }}
                  className="w-full bg-teal-500 text-white py-3 rounded-lg font-semibold hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-refresh-line mr-2"></i>
                  Try Again
                </button>
                <button
                  onClick={() => {
                    console.log('📧 User clicked Contact Support');
                    setShowErrorModal(false);
                    window.location.href = 'mailto:support@example.com?subject=Payment Issue&body=' + encodeURIComponent(errorMessage);
                  }}
                  className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-customer-service-2-line mr-2"></i>
                  Contact Support
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            PERFORMANCE &amp; SAFETY EQUIPMENT
          </div>
          <h2 className="text-5xl font-bold text-gray-900 mb-4">
            PREMIUM <span className="text-teal-500">GEAR STORE</span>
          </h2>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto">
            Top-tier equipment and uniforms from leading brands. Automated ordering, direct shipping, and commission tracking built-in.
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl p-6 mb-12 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">Filter by Sport</label>
              <div className="flex flex-wrap gap-2">
                {sports.map(sport => (
                  <button
                    key={sport}
                    onClick={() => setSelectedSport(sport)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      selectedSport === sport
                        ? 'bg-teal-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {sport}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">Filter by Category</label>
              <div className="flex flex-wrap gap-2">
                {categories.map(category => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      selectedCategory === category
                        ? 'bg-teal-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProducts.map(product => (
            <div
              key={product.id}
              className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group cursor-pointer"
              data-product-shop
              onClick={() => setSelectedProduct(product)}
            >
              <div className="relative overflow-hidden bg-gray-50">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-500 cursor-zoom-in"
                  onClick={(e) => { e.stopPropagation(); setLightboxImage({ src: product.image, name: product.name }); }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgZmlsbD0iI2Y3ZjdmNyIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjE2IiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+SW1hZ2UgTm90IEF2YWlsYWJsZTwvdGV4dD48L3N2Zz4=';
                  }}
                />
                <div
                  className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 cursor-zoom-in"
                  onClick={() => setLightboxImage({ src: product.image, name: product.name })}
                >
                  <div className="bg-black/50 rounded-full w-12 h-12 flex items-center justify-center">
                    <i className="ri-zoom-in-line text-white text-xl"></i>
                  </div>
                </div>
                <div className="absolute top-4 right-4 bg-white px-3 py-1 rounded-full text-xs font-bold text-gray-900">
                  {product.inStock ? 'IN STOCK' : 'PRE-ORDER'}
                </div>
                <div className="absolute top-4 left-4 bg-teal-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                  {product.performanceBoost}
                </div>
              </div>

              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-xs text-gray-500 font-semibold mb-1">{product.brand}</p>
                    <h3 className="text-lg font-bold text-gray-900">{product.name}</h3>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-gray-900">${product.price}</div>
                  </div>
                </div>

                <div className="flex items-center space-x-2 mb-4">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <i
                        key={i}
                        className={`ri-shield-${i < product.safetyRating ? 'check' : 'line'}-fill text-sm ${
                          i < product.safetyRating ? 'text-teal-500' : 'text-gray-300'
                        }`}
                      ></i>
                    ))}
                  </div>
                  <span className="text-xs text-gray-500">Safety Rating</span>
                </div>

                <div className="mb-4">
                  <div className="flex flex-wrap gap-1 mb-2">
                    {product.sport.map(sport => (
                      <span
                        key={sport}
                        className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs font-medium"
                      >
                        {sport}
                      </span>
                    ))}
                    <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs font-medium">
                      {product.category}
                    </span>
                  </div>
                </div>

                <ul className="space-y-2 mb-6">
                  {product.features.slice(0, 3).map((feature, idx) => (
                    <li key={idx} className="flex items-start space-x-2 text-sm text-gray-600">
                      <i className="ri-check-line text-teal-500 mt-0.5 flex-shrink-0"></i>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* Basket Buttons */}
                <div className="space-y-2 mb-4">
                  {isInBasket(product.id) ? (
                    <button
                      onClick={(e) => { e.stopPropagation(); removeFromBasket(product.id); }}
                      className="w-full py-3 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2 whitespace-nowrap cursor-pointer bg-red-500 text-white hover:bg-red-600"
                    >
                      <i className="ri-delete-bin-line"></i>
                      <span>Remove from Basket</span>
                    </button>
                  ) : (
                    <button
                      onClick={(e) => { e.stopPropagation(); addToBasket(product.id); }}
                      className="w-full py-3 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2 whitespace-nowrap cursor-pointer bg-gray-900 text-white hover:bg-gray-800"
                    >
                      <i className="ri-shopping-basket-line"></i>
                      <span>Add to Basket</span>
                    </button>
                  )}
                </div>

                <button
                  onClick={(e) => { e.stopPropagation(); handleOrder(product); }}
                  disabled={processingOrder === product.id}
                  className={`w-full py-3 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2 whitespace-nowrap cursor-pointer ${
                    processingOrder === product.id
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : 'bg-teal-500 text-white hover:bg-teal-600'
                  }`}
                >
                  {processingOrder === product.id ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-shopping-cart-line"></i>
                      <span>Order Now - Auto Ship</span>
                    </>
                  )}
                </button>

                <p className="text-xs text-gray-500 text-center mt-3">
                  Automated fulfillment • Commission tracked
                </p>
              </div>
            </div>
          ))}
        </div>

        {filteredProducts.length === 0 && (
          <div className="text-center py-16">
            <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
            <p className="text-gray-500 text-lg">No products found matching your filters</p>
          </div>
        )}

        {/* Info Banner */}
        <div className="mt-16 bg-gradient-to-r from-teal-500 to-teal-600 rounded-2xl p-12 text-center">
          <h3 className="text-3xl font-bold text-white mb-4">Streamlined Order Processing</h3>
          <p className="text-teal-50 text-lg mb-8 max-w-3xl mx-auto">
            All orders are automatically processed and shipped directly from manufacturers. Simple, fast, and hassle-free equipment procurement.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <i className="ri-truck-line text-4xl text-white mb-3"></i>
              <h4 className="text-white font-bold mb-2">Direct Shipping</h4>
              <p className="text-teal-50 text-sm">From manufacturer to your team</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <i className="ri-shopping-cart-line text-4xl text-white mb-3"></i>
              <h4 className="text-white font-bold mb-2">Easy Ordering</h4>
              <p className="text-teal-50 text-sm">Simple checkout process</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <i className="ri-shield-check-line text-4xl text-white mb-3"></i>
              <h4 className="text-white font-bold mb-2">Secure Payments</h4>
              <p className="text-teal-50 text-sm">Encrypted &amp; verified</p>
            </div>
          </div>
        </div>

        {/* Product Detail Modal */}
        {selectedProduct && (
          <ProductDetailModal
            product={selectedProduct}
            onClose={() => setSelectedProduct(null)}
            onOrder={(product) => { setSelectedProduct(null); handleOrder(product); }}
            onAddToBasket={(id) => addToBasket(id)}
            onRemoveFromBasket={(id) => removeFromBasket(id)}
            isInBasket={isInBasket(selectedProduct.id)}
            isProcessing={processingOrder === selectedProduct.id}
          />
        )}
      </div>
    </section>
  );
}
